/**
 * Converts between the block-array shape BlogPostPage renders
 * ({type:"p"|"h2"|"h3"|"ul"|"quote", ...}) and a plain-text form an
 * admin can type into a single textarea — no rich-text editor needed.
 *
 * Syntax: "## " = h2, "### " = h3, "> " = quote, "- " = list item
 * (consecutive "- " lines group into one ul), blank line = paragraph
 * break, everything else = a paragraph.
 */
export function parseBodyText(text) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let para = [];
  let listItems = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: "p", text: para.join(" ").trim() });
      para = [];
    }
  };
  const flushList = () => {
    if (listItems.length) {
      blocks.push({ type: "ul", items: listItems.slice() });
      listItems = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();

    if (line === "") {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith("## ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h2", text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith("### ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h3", text: line.slice(4).trim() });
      continue;
    }
    if (line.startsWith("> ")) {
      flushPara();
      flushList();
      blocks.push({ type: "quote", text: line.slice(2).trim() });
      continue;
    }
    if (line.startsWith("- ")) {
      flushPara();
      listItems.push(line.slice(2).trim());
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();

  return blocks;
}

export function blocksToText(blocks) {
  if (!blocks?.length) return "";
  return blocks
    .map((b) => {
      if (b.type === "h2") return `## ${b.text}`;
      if (b.type === "h3") return `### ${b.text}`;
      if (b.type === "quote") return `> ${b.text}`;
      if (b.type === "ul") return b.items.map((i) => `- ${i}`).join("\n");
      return b.text;
    })
    .join("\n\n");
}

/** URL-safe slug from a title — used as the Firestore doc id. */
export function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}
